<template>
    <LoaderComponent v-if="loading" />

    <div class="rounded-2xl shadow border border-black mb-6 overflow-hidden" v-for="address in addresses"
        :key="address">
        <div class="bg-[#F6E2B5] px-4 py-3 flex items-center justify-between">
            <div>
                <div class="flex items-center gap-2">
                    <span
                        class="text-xs sm:text-sm whitespace-nowrap text-white bg-gradient-to-r from-yellow-400 to-amber-600 px-2 py-1 sm:px-3 sm:py-2 rounded-2xl shadow-sm transition-all duration-200 ease-in-out hover:shadow-md hover:-translate-y-0.5">
                        {{ address.type == 'ship_from' ? 'Sender Address' : 'Receiver Addresses' }}
                    </span>
                </div>
            </div>

            <div class="flex items-center gap-1 sm:gap-3">
                <span
                    class="text-xs sm:text-sm font-semibold px-2 sm:px-3 py-0.5 border border-[#8B5A00] rounded-full text-[#8B5A00] flex items-center gap-1">
                    <i class="fa-solid fa-location-dot"></i>
                    {{ address.is_residential == 1 ? 'Residential' : 'Business' }}
                </span>

                <!-- <div class="relative">
                    <button
                        class="peer w-5 h-6 sm:w-9 sm:h-9 rounded-md bg-white shadow flex items-center justify-center border border-gray-300">
                        <i class="fa-solid fa-ellipsis-vertical text-gray-600"></i>
                    </button>
                    <div
                        class="hidden peer-focus:block absolute right-0 mt-2 w-24 bg-white border border-gray-300 shadow rounded-md py-1">
                        <button class="block w-full text-left text-sm px-3 py-1 hover:bg-gray-100 text-gray-700">
                            Edit
                        </button>
                    </div>
                </div> -->
            </div>
        </div>

        <!-- Card Body -->
        <div class="px-6 py-4 grid sm:grid-cols-2 gap-x-14 gap-y-4 text-sm text-gray-700">

            <!-- Column 1 -->
            <div class="space-y-1 whitespace-nowrap">
                <p><span class="font-semibold">Company Name:</span> {{ address.company_name }}</p>
                <p><span class="font-semibold">Full Name:</span> {{ address.fullname }}</p>
                <p v-if="address.tax_no"><span class="font-semibold">Tax ID:</span> {{ address.tax_no }}</p>
                <p><span class="font-semibold">Phone:</span> {{ address.phone }}</p>
                <p><span class="font-semibold">Email:</span> {{ address.email }}</p>
                <p><span class="font-semibold">Country:</span> {{ address?.country?.name }}</p>
            </div>

            <!-- Column 2 -->
            <div class="space-y-1 whitespace-nowrap">
                <p><span class="font-semibold">Address Line 1:</span> {{ address.address }}</p>
                <p v-if="address.address_2"><span class="font-semibold">Address Line 2:</span> {{ address.address_2 }}
                </p>
                <p v-if="address.address_3"><span class="font-semibold">Address Line 3:</span> {{ address.address_3 }}
                </p>
                <p v-if="address.state"><span class="font-semibold">State:</span> {{ address.state }}</p>
                <p><span class="font-semibold">City:</span> {{ address.city }}</p>
                <p><span class="font-semibold">Zip Code:</span> {{ address.zip_code }}</p>
            </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-4 py-3 border-t flex justify-end gap-2">
            <!-- <button
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition">
                <i class="fa-solid fa-trash"></i> Delete
            </button> -->

            <button @click="editAddressModal(address.id, address.type)"
                class="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-yellow-400 to-amber-600 rounded-full shadow hover:brightness-110 transition">
                <i class="fa-solid fa-pen"></i> Edit
            </button>
        </div>
    </div>


    <template v-if="address_modal">
        <!-- Overlay -->
        <div class="fixed inset-0 z-[1000] bg-black bg-opacity-50" @click="closeAddressModal"></div>

        <!-- Modal / Sidebar -->
        <div
            class="fixed top-0 right-0 z-[1001] h-full w-full sm:w-1/2 md:w-1/2 lg:w-1/2 bg-white shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col">
            <!-- Header -->
            <div class="flex items-center justify-between px-4 py-3 border-b">
                <h3 class="text-lg font-bold">
                    {{ form.type === 'ship_from' ? 'Ship From' : 'Ship To' }} Address
                </h3>
                <button @click="closeAddressModal" class="text-gray-500 hover:text-gray-900">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                        </path>
                    </svg>
                </button>
            </div>

            <!-- Body -->
            <div class="relative py-5 px-5 overflow-y-auto pb-28 sm:pb-20"
                :class="{ 'overflow-hidden pointer-events-none': loading }">

                <LoaderComponent v-if="loading" />

                <div v-if="errors.message">
                    <DangerAlert :message="errors.message" />
                </div>

                <form @submit.prevent="handleSubmit" class="space-y-4">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Company
                                Name</label>
                            <input type="text" v-model="form.company_name"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.company_name }}</span>
                        </div>
                        <div>
                            <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Full
                                Name *</label>
                            <input type="text" v-model="form.fullname"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.fullname }}</span>
                        </div>
                        <div>
                            <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Is
                                Residential?</label>
                            <select v-model="form.is_residential"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500">
                                <option :value="true">Yes</option>
                                <option :value="false">No</option>
                            </select>
                            <span class="text-red-600">{{ errors?.errors?.is_residential }}</span>
                        </div>
                        <div>
                            <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Address
                                line 1 *</label>
                            <input type="text" v-model="form.address" id="address1"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.address }}</span>
                        </div>
                        <div>
                            <label for="address2" class="block mb-2 text-sm font-medium text-gray-900">Address
                                line 2</label>
                            <input type="text" v-model="form.address_2" id="address2"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.address_2 }}</span>
                        </div>
                        <div>
                            <label for="address2" class="block mb-2 text-sm font-medium text-gray-900">Address
                                line 3</label>
                            <input type="text" v-model="form.address_3" id="address2"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.address_3 }}</span>
                        </div>
                        <div>
                            <label for="country" class="block mb-2 text-sm font-medium text-gray-900">Country
                                *</label>
                            <select v-model="form.country_id"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5">
                                <template v-if="form.type == 'ship_to'">
                                    <option value="">Select</option>
                                </template>
                                <template v-for="country in countries" :key="country.id">
                                    <option :value="country.id">{{ country.name }}</option>
                                </template>
                            </select>
                            <span class="text-red-600">{{ errors?.errors?.country_id }}</span>
                        </div>
                        <div>
                            <label for="country" class="block mb-2 text-sm font-medium text-gray-900">
                                <span>State</span>
                                <span v-if="form.country_id == 226">*</span>
                            </label>

                            <template v-if="form.country_id == 226">
                                <select v-model="form.state"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5">
                                    <template v-for="state in states" :key="state.id">
                                        <option :value="state.abbr">{{ state.name }}</option>
                                    </template>
                                </select>
                            </template>

                            <template v-else>
                                <input type="text" v-model="form.state"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                    placeholder="">
                            </template>

                            <span class="text-red-600">{{ errors?.errors?.state }}</span>
                        </div>
                        <div>
                            <label for="city" class="block mb-2 text-sm font-medium text-gray-900">City
                                *</label>
                            <input type="text" v-model="form.city"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.city }}</span>
                        </div>
                        <div>
                            <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">
                                Zip
                                code *</label>
                            <input type="text" v-model="form.zip_code"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.zip_code }}</span>
                        </div>
                        <div>
                            <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">Tax
                                ID</label>
                            <input type="text" v-model="form.tax_no"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.tax_no }}</span>
                        </div>
                        <div>
                            <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">Phone
                                *</label>
                            <input type="text" v-model="form.phone"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.phone }}</span>
                        </div>
                        <div>
                            <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">Email
                                *</label>
                            <input type="email" v-model="form.email"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                                placeholder="">
                            <span class="text-red-600">{{ errors?.errors?.email }}</span>
                        </div>
                        <div>
                            <label for="country" class="block mb-2 text-sm font-medium text-gray-900">Signature
                                Type
                                *</label>
                            <select v-model="form.signature_type_id"
                                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5">
                                <template v-for="signature_type in signature_types" :key="signature_type.id">
                                    <option :value="signature_type.id">{{ signature_type.name }}</option>
                                </template>
                            </select>
                            <span class="text-red-600">{{ errors?.errors?.signature_type_id }}</span>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Footer / Buttons -->
            <div
                class="flex justify-end gap-2 px-4 py-3 border-t bg-white sm:relative fixed bottom-0 left-0 w-full sm:w-auto sm:bottom-auto sm:left-auto sm:px-4 sm:py-3">
                <button @click="closeAddressModal"
                    class="px-5 py-2 rounded-full border border-gray-300 bg-white hover:bg-gray-100">
                    Cancel
                </button>
                <button @click="handleSubmit"
                    class="px-5 py-2 rounded-full bg-yellow-400 hover:bg-yellow-500 text-white">
                    {{ loading ? "Loading ..." : "Save" }}
                </button>
            </div>
        </div>
    </template>

    <DefaultModal title="Hello World">
        <form @submit.prevent="handleSubmit" class="space-y-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Company
                        Name</label>
                    <input type="text" v-model="form.company_name"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.company_name }}</span>
                </div>
                <div>
                    <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Full
                        Name *</label>
                    <input type="text" v-model="form.fullname"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.fullname }}</span>
                </div>
                <div>
                    <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Is
                        Residential?</label>
                    <select v-model="form.is_residential"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500">
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                    <span class="text-red-600">{{ errors?.errors?.is_residential }}</span>
                </div>
                <div>
                    <label for="address1" class="block mb-2 text-sm font-medium text-gray-900">Address
                        line 1 *</label>
                    <input type="text" v-model="form.address" id="address1"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 blue-500"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.address }}</span>
                </div>
                <div>
                    <label for="address2" class="block mb-2 text-sm font-medium text-gray-900">Address
                        line 2</label>
                    <input type="text" v-model="form.address_2" id="address2"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.address_2 }}</span>
                </div>
                <div>
                    <label for="address2" class="block mb-2 text-sm font-medium text-gray-900">Address
                        line 3</label>
                    <input type="text" v-model="form.address_3" id="address2"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.address_3 }}</span>
                </div>
                <div>
                    <label for="country" class="block mb-2 text-sm font-medium text-gray-900">Country
                        *</label>
                    <select v-model="form.country_id"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5">
                        <template v-if="form.type == 'ship_to'">
                            <option value="">Select</option>
                        </template>
                        <template v-for="country in countries" :key="country.id">
                            <option :value="country.id">{{ country.name }}</option>
                        </template>
                    </select>
                    <span class="text-red-600">{{ errors?.errors?.country_id }}</span>
                </div>
                <div>
                    <label for="country" class="block mb-2 text-sm font-medium text-gray-900">
                        <span>State</span>
                        <span v-if="form.country_id == 226">*</span>
                    </label>

                    <template v-if="form.country_id == 226">
                        <select v-model="form.state"
                            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5">
                            <template v-for="state in states" :key="state.id">
                                <option :value="state.abbr">{{ state.name }}</option>
                            </template>
                        </select>
                    </template>

                    <template v-else>
                        <input type="text" v-model="form.state"
                            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                            placeholder="">
                    </template>

                    <span class="text-red-600">{{ errors?.errors?.state }}</span>
                </div>
                <div>
                    <label for="city" class="block mb-2 text-sm font-medium text-gray-900">City
                        *</label>
                    <input type="text" v-model="form.city"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.city }}</span>
                </div>
                <div>
                    <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">
                        Zip
                        code *</label>
                    <input type="text" v-model="form.zip_code"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.zip_code }}</span>
                </div>
                <div>
                    <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">Tax
                        ID</label>
                    <input type="text" v-model="form.tax_no"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.tax_no }}</span>
                </div>
                <div>
                    <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">Phone
                        *</label>
                    <input type="text" v-model="form.phone"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.phone }}</span>
                </div>
                <div>
                    <label for="postal-code" class="block mb-2 text-sm font-medium text-gray-900">Email
                        *</label>
                    <input type="email" v-model="form.email"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5"
                        placeholder="">
                    <span class="text-red-600">{{ errors?.errors?.email }}</span>
                </div>
                <div>
                    <label for="country" class="block mb-2 text-sm font-medium text-gray-900">Signature
                        Type
                        *</label>
                    <select v-model="form.signature_type_id"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5">
                        <template v-for="signature_type in signature_types" :key="signature_type.id">
                            <option :value="signature_type.id">{{ signature_type.name }}</option>
                        </template>
                    </select>
                    <span class="text-red-600">{{ errors?.errors?.signature_type_id }}</span>
                </div>
            </div>
        </form>
    </DefaultModal>


</template>

<script>
import LoaderComponent from "@/components/Global/Modals/LoaderComponent.vue";
import DefaultModal from "@/components/Global/Modals/DefaultModal.vue";

export default {
    name: "AccountAddress",
    components: { LoaderComponent, DefaultModal },
    data() {
        return {
            addresses: [],
            loading: false,
            project_url: process.env.VUE_APP_PROJECT_URL,
            address_modal: false,
            form: {
                company_name: "",
                fullname: "",
                is_residential: "",
                country_id: "",
                state: "",
                city: "",
                zip_code: "",
                phone: "",
                email: "",
                address: "",
                address_2: "",
                address_3: "",
                tax_no: "",
                type: "",
                signature_type_id: "",
                update_flag: false,
                address_id: null,
            },
            errors: {}
        };
    },

    methods: {
        fetchAddresses() {
            this.loading = true;
            const url = "fetch-address-list";
            this.$axios
                .post(url, null, {
                    headers: this.$headers,
                })
                .then((response) => {
                    this.addresses = response.data.data.addresses;
                    console.log(response);
                    this.loading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.loading = false;
                });
        },
        editAddressModal(address_id, address_type) {
            document.body.classList.add('overflow-hidden')

            this.form = {};
            this.errors = {};

            this.form.type = address_type;
            this.form.address_id = address_id;
            this.form.update_flag = true;
            this.address_modal = true;
            this.fetchRecords();

            const url = "fetch-address"
            this.$axios
                .post(url, {
                    address_id: address_id
                }, {
                    headers: this.$headers,
                })
                .then((response) => {
                    console.log(response);
                    var address = response.data.data.address;
                    this.form.company_name = address.company_name;
                    this.form.fullname = address.fullname;
                    this.form.is_residential = address.is_residential == 1 ? true : false;
                    this.form.country_id = address.country_id;
                    this.form.state = address.state;
                    this.form.city = address.city;
                    this.form.zip_code = address.zip_code;
                    this.form.phone = address.phone;
                    this.form.email = address.email;
                    this.form.address = address.address;
                    this.form.address_2 = address.address_2;
                    this.form.address_3 = address.address_3;
                    this.form.tax_no = address.tax_no;
                    this.form.signature_type_id = address.signature_type_id;
                })
                .catch((error) => {
                    console.log(error);
                    // window.scrollTo(0, 0);
                });
        },
        handleSubmit() {
            this.loading = true;
            this.errors = {};

            this.$axios
                .post("address/store", this.form, {
                    headers: this.$headers
                })
                .then((response) => {
                    if (response.data.status == true) {
                        this.closeAddressModal();
                        this.fetchAddresses();
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.errors = error.response.data;
                    console.log(error.response.data);
                    this.loading = false;
                    window.scrollTo(0, 0);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        closeAddressModal() {
            this.address_modal = false;
            document.body.classList.remove('overflow-hidden')

            this.form = {};
            this.errors = {};
        },
        fetchRecords() {
            const url = "data"
            this.$axios
                .post(url, {
                    headers: this.$headers,
                })
                .then((response) => {
                    this.countries = [];
                    for (const country of response.data.data.countries) {
                        if (this.form.type === "ship_from") {
                            if (country.iso === 'US') {
                                this.countries.push(country);
                            }
                        } else {
                            this.countries.push(country);
                        }
                    }
                    this.states = response.data.data.states;
                    this.signature_types = response.data.data.signature_types;
                })
                .catch((error) => {
                    console.log(error);
                    window.scrollTo(0, 0);
                });
        },
    },
    mounted() {
        this.fetchAddresses();
    },
};
</script>